import { useMemo, useState } from "react";
import { ArrowRight, GitBranch, Play, Pencil, Save, X } from "lucide-react";
import Editor from "react-simple-code-editor";
import Prism from "prismjs";
import "prismjs/components/prism-json";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import PageHeader from "@/layout/PageHeader";
import { WorkflowsSkeleton } from "@/components/PageSkeletons";
import { API, authHeaders } from "@/lib/api";
import { fetchers, qk } from "@/lib/queryClient";
import { toast } from "sonner";

function StepChip({ agent }) {
  return (
    <div className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-hm-border bg-hm-bg-1 mono text-[10px] uppercase tracking-widest text-hm-fg-muted">
      <span className="h-1.5 w-1.5 rounded-full bg-hm-accent-dim" />
      {agent}
    </div>
  );
}

function WorkflowCard({ wf, onRun, onEdit }) {
  return (
    <div
      data-testid={`workflow-card-${wf.slug}`}
      className="hm-surface p-6 flex flex-col gap-4"
    >
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-lg bg-hm-bg-2 border border-hm-border-strong flex items-center justify-center">
          <GitBranch className="h-5 w-5 text-hm-accent" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-hm-fg">{wf.name}</span>
            {wf.is_active && (
              <span className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-hm-accent-glow text-hm-accent">
                active
              </span>
            )}
            <span className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full border border-hm-border text-hm-fg-subtle">
              v{wf.definition_version}
            </span>
          </div>
          <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle mt-0.5">
            {wf.slug}
          </div>
        </div>
        <button
          data-testid={`workflow-edit-${wf.slug}`}
          onClick={() => onEdit(wf)}
          className="h-8 w-8 rounded-md flex items-center justify-center text-hm-fg-subtle hover:text-hm-fg hover:bg-hm-bg-1"
          aria-label={`Edit ${wf.name}`}
          title="Edit definition"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
        <button
          data-testid={`workflow-run-${wf.slug}`}
          onClick={() => onRun(wf)}
          className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-hm-accent text-hm-bg hover:bg-hm-accent-dim transition-colors text-xs font-medium"
        >
          <Play className="h-3 w-3" />
          Run
        </button>
      </div>

      <p className="text-xs text-hm-fg-muted leading-relaxed">{wf.description}</p>

      <div className="flex items-center gap-2 flex-wrap pt-2">
        {wf.steps.map((s, i) => (
          <div key={i} className="flex items-center gap-2">
            <StepChip agent={s.agent} />
            {i < wf.steps.length - 1 && (
              <ArrowRight className="h-3.5 w-3.5 text-hm-fg-subtle" />
            )}
          </div>
        ))}
      </div>

      {wf.trigger_prompt && (
        <div className="pt-3 border-t border-hm-border">
          <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle mb-1">
            trigger prompt
          </div>
          <div className="mono text-[11px] text-hm-fg-muted leading-relaxed">
            {wf.trigger_prompt}
          </div>
        </div>
      )}
    </div>
  );
}

function JSONEditorModal({ wf, onClose, onSaved }) {
  const initial = useMemo(() => JSON.stringify(wf.definition, null, 2), [wf]);
  const [code, setCode] = useState(initial);
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);

  const validate = (raw) => {
    try {
      JSON.parse(raw);
      setErr(null);
      return true;
    } catch (e) {
      setErr(e.message);
      return false;
    }
  };

  const save = async () => {
    if (!validate(code)) {
      toast.error("Invalid JSON");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/workflows/${wf.id}`, {
        method: "PATCH",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ definition: JSON.parse(code) }),
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || `HTTP ${res.status}`);
      }
      const updated = await res.json();
      toast.success(`Saved — v${updated.definition_version}`);
      onSaved(updated);
    } catch (e) {
      let msg = e.message;
      try {
        const parsed = JSON.parse(e.message);
        if (parsed.detail) msg = parsed.detail;
      } catch {
        /* leave msg as-is */
      }
      toast.error(`Save failed: ${msg}`);
      setErr(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-40 bg-[var(--hm-overlay)] backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="workflow-editor-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-4xl h-[80vh] bg-hm-bg-2 border border-hm-border-strong rounded-lg flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="h-14 shrink-0 flex items-center px-5 border-b border-hm-border gap-2">
          <Pencil className="h-4 w-4 text-hm-accent" />
          <span className="text-sm text-hm-fg">Edit definition · {wf.name}</span>
          <span className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle">
            v{wf.definition_version}
          </span>
          <button
            onClick={onClose}
            data-testid="workflow-editor-close"
            className="ml-auto h-8 w-8 rounded-md flex items-center justify-center text-hm-fg-subtle hover:text-hm-fg hover:bg-hm-bg-1"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="flex-1 min-h-0 overflow-auto">
          <Editor
            value={code}
            onValueChange={(v) => {
              setCode(v);
              validate(v);
            }}
            highlight={(c) => Prism.highlight(c, Prism.languages.json, "json")}
            padding={16}
            data-testid="workflow-editor-textarea"
            textareaId="workflow-editor-textarea"
            style={{
              fontFamily: "JetBrains Mono, monospace",
              fontSize: 12,
              minHeight: "100%",
              lineHeight: 1.6,
              backgroundColor: "var(--hm-bg-1)",
              color: "var(--hm-fg)",
              caretColor: "var(--hm-accent)",
            }}
          />
        </div>
        {err && (
          <div
            className="shrink-0 px-5 py-2 border-t border-hm-danger/40 bg-hm-danger/10 mono text-[11px] text-hm-danger"
            data-testid="workflow-editor-error"
          >
            {err}
          </div>
        )}
        <footer className="h-14 shrink-0 flex items-center justify-end gap-2 px-5 border-t border-hm-border">
          <button
            onClick={onClose}
            className="px-3 h-8 rounded-md text-sm text-hm-fg-muted hover:text-hm-fg hover:bg-hm-bg-1"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving || !!err}
            data-testid="workflow-editor-save"
            className="inline-flex items-center gap-1.5 px-4 h-8 rounded-md bg-hm-accent text-hm-bg text-sm font-medium disabled:opacity-40 hover:bg-hm-accent-dim"
          >
            <Save className="h-3.5 w-3.5" />
            {saving ? "Saving…" : "Save"}
          </button>
        </footer>
      </div>
    </div>
  );
}

export default function WorkflowsPage() {
  const [pending, setPending] = useState(null);
  const [editing, setEditing] = useState(null);
  const queryClient = useQueryClient();

  const { data: wfs, isPending } = useQuery({
    queryKey: qk.workflows(),
    queryFn: fetchers.workflows,
  });

  const runWorkflow = (wf) => {
    setPending(wf);
    try {
      window.dispatchEvent(
        new CustomEvent("hm:open-chat-with", { detail: { message: wf.trigger_prompt } }),
      );
    } finally {
      setTimeout(() => setPending(null), 1500);
    }
  };

  const onSaved = (updated) => {
    // Update the cached list in-place — no full refetch needed.
    queryClient.setQueryData(qk.workflows(), (prev) =>
      Array.isArray(prev) ? prev.map((w) => (w.id === updated.id ? updated : w)) : prev
    );
    setEditing(null);
  };

  return (
    <div data-testid="workflows-page">
      <PageHeader
        title="Workflows"
        badge="Live"
        subtitle="Compose agents into repeatable pipelines. Click Run to trigger from the chat surface."
      />
      {isPending || !wfs ? (
        <WorkflowsSkeleton n={4} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="workflows-grid">
          {wfs.map((wf) => (
            <WorkflowCard
              key={wf.slug}
              wf={wf}
              onRun={runWorkflow}
              onEdit={setEditing}
            />
          ))}
        </div>
      )}
      {pending && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-40 mono text-[11px] uppercase tracking-widest text-hm-accent bg-hm-bg-2 border border-hm-accent-dim px-3 py-1.5 rounded-full">
          triggered · {pending.slug}
        </div>
      )}
      {editing && (
        <JSONEditorModal wf={editing} onClose={() => setEditing(null)} onSaved={onSaved} />
      )}
    </div>
  );
}
